import { useMemo } from 'react';

import { trackEvent } from 'analytics/appInsights';
import type { Fetcher } from 'api/fetcher';
import { useApi } from 'api/useApi';
import { ENV_VARS } from 'app/config/environment-vars';
import type { PropertyValue } from 'state/data/dataState';
import type { EPropertyTypes } from 'state/selectedTemplate/selectedTemplateStore';
import type { SemNode } from 'types/data/semNode';
import { pathJoin } from 'utils/strings/pathJoin';

import type { IDataEventBase, ProjectNode, UserSpecificTemplate } from '../client-dataapi';
import type { ApiAddedRelation, ApiDecompositionNode, ApiNewNode, ApiNodeDetail } from './types';

export const useProjectsApi = () => {
  const fetchFunction = useApi(ENV_VARS.DATA_URL, 'DataApiClient');

  return useMemo(() => projectsApi(fetchFunction), [fetchFunction]);
};

export const projectsApi = (fetchFunction: Fetcher) => {
  const getById = (projectId: string) => {
    return fetchFunction<ProjectNode>(getProjectUrl(projectId));
  };

  const getProjectTemplate = (projectId: string) => {
    return fetchFunction<UserSpecificTemplate>(getProjectTemplateUrl(projectId));
  };

  const updateProjectNodeProperty = (
    projectId: string,
    nodeId: string,
    propertyId: string,
    payload: { value: PropertyValue; propertyType: EPropertyTypes; proposalId?: string },
    trigger: string
  ) => {
    trackEvent(`Update Property Value (${trigger})`, 'Project', { projectId });
    return fetchFunction<IDataEventBase>(getProjectNodeProperty(projectId, nodeId, propertyId), {
      method: 'POST',
      body: JSON.stringify(payload)
    });
  };

  const getNodeDetail = (projectId: string, templateNodeId: string, dataNodeId: string, proposalId?: string) => {
    const params = new URLSearchParams({ templateNodeId });
    if (proposalId) {
      params.set('proposalId', proposalId);
    }
    const url = `${getNodeDetailUrl(projectId, dataNodeId)}?${params}`;
    return fetchFunction<ApiNodeDetail>(url);
  };

  const createRootNode = (
    resultSetEntryId: string,
    projectId: string,
    queryId: string,
    proposalId: string | undefined
  ) => {
    return fetchFunction<IDataEventBase>(getCreateRootNodeUrl(projectId, queryId), {
      method: 'POST',
      body: JSON.stringify({
        resultSetEntryId,
        proposalId,
        properties: {}
      })
    });
  };

  const assignLibraryNodeToDataNode = (
    projectId: string,
    dataNodeId: string,
    templateNodeId: string,
    libraryNodeId: string,
    proposalId?: string
  ) => {
    const params = new URLSearchParams({ templateNodeId });

    if (proposalId) {
      params.set('proposalId', proposalId);
    }

    const url = `${pathJoin('api/projects', projectId, 'nodes', dataNodeId)}?${params}`;

    return fetchFunction<{ properties: SemNode['Properties'] }>(url, {
      method: 'PATCH',
      body: JSON.stringify({ LibraryNodeId: libraryNodeId })
    });
  };

  const getTreeRootNodes = (
    projectId: string,
    relationId: string,
    technicalName: string,
    limit = 100,
    offset = 0,
    proposalId?: string
  ) => {
    const orderBy = 'Created';
    const isReverse = true;
    const expandRelationId = relationId;
    // Hardcoded for now because we only cover one relation
    const isExpandRelationReverse = true;
    const url = `api/projects/${projectId}/nodes/unconnected?technicalName=${technicalName}&orderBy=${orderBy}&limit=${limit}&offset=${offset}&relationId=${relationId}&isReverse=${isReverse}&expandRelationId=${expandRelationId}&isExpandRelationReverse=${isExpandRelationReverse}${proposalId ? `&proposalId=${proposalId}` : ''}`;
    return fetchFunction<ApiDecompositionNode[]>(url);
  };

  const getTreeConnectedNodes = (
    projectId: string,
    parentNodeId: string,
    relationId: string,
    technicalName: string,
    limit = 100,
    offset = 0,
    proposalId?: string
  ) => {
    const orderBy = 'Created';
    const isReverse = true;
    const expandRelationId = relationId;
    // Hardcoded for now because we only cover one relation
    const isExpandRelationReverse = true;
    const url = `api/projects/${projectId}/nodes/${parentNodeId}/connected?relationId=${relationId}&technicalName=${technicalName}&orderBy=${orderBy}&limit=${limit}&offset=${offset}&parentNodeTechnicalName=${technicalName}&isReverse=${isReverse}&expandRelationId=${expandRelationId}&isExpandRelationReverse=${isExpandRelationReverse}${proposalId ? `&proposalId=${proposalId}` : ''}`;
    return fetchFunction<ApiDecompositionNode[]>(url);
  };

  const addNode = (
    projectId: string,
    templateNodeId: string,
    proposalId?: string,
    endDataNodeId?: string,
    templateRelationId?: string
  ) => {
    const params = new URLSearchParams();

    if (proposalId) {
      params.set('proposalId', proposalId);
    }

    if (endDataNodeId) {
      params.set('parentNodeId', endDataNodeId);
    }

    if (templateRelationId) {
      params.set('relationId', templateRelationId);
    }

    const query = params.toString();
    const url = `api/projects/${projectId}/nodes${query ? `?${query}` : ''}`;
    return fetchFunction<ApiNewNode>(url, {
      method: 'POST',
      body: JSON.stringify({ templateNodeId, properties: {} })
    });
  };

  const addRelation = (
    projectId: string,
    startDataNodeId: string,
    endNodeId: string,
    templateRelationId: string,
    proposalId?: string
  ) => {
    const url = `api/projects/${projectId}/nodes/${startDataNodeId}/relations${proposalId ? `?proposalId=${proposalId}` : ''}`;
    return fetchFunction<ApiAddedRelation>(url, {
      method: 'POST',
      body: JSON.stringify({ endNodeId, templateRelationId })
    });
  };

  const deleteRelation = (
    projectId: string,
    startDataNodeId: string,
    templateRelationId: string,
    endDataNodeId: string,
    proposalId?: string
  ) => {
    const url = `api/projects/${projectId}/nodes/${startDataNodeId}/relations/${templateRelationId}/nodes/${endDataNodeId}${proposalId ? `?proposalId=${proposalId}` : ''}`;
    return fetchFunction<IDataEventBase>(url, {
      method: 'DELETE'
    });
  };

  const updateNode = (
    projectId: string,
    nodeId: string,
    templateNodeId: string,
    properties: Record<string, unknown>,
    proposalId?: string
  ) => {
    const query = proposalId ? `?proposalId=${proposalId}` : '';
    const url = `api/projects/${projectId}/nodes/${nodeId}${query}`;
    return fetchFunction<ApiNewNode>(url, {
      method: 'PUT',
      body: JSON.stringify({ templateNodeId, properties })
    });
  };

  return {
    /** Get the project by Id. */
    getById,
    /** Get the project template by project Id. */
    getProjectTemplate,
    /** Update a project node property. */
    updateProjectNodeProperty,
    /** Get a single node's permitted property values (permission-safe detail endpoint). */
    getNodeDetail,
    /** Create a root node. */
    createRootNode,
    /** Assign a library node to a data node. */
    assignLibraryNodeToDataNode,
    /** Get the tree of a node. */
    getTreeRootNodes,
    /** Get the tree children of a node. */
    getTreeConnectedNodes,
    /** Add a node. */
    addNode,
    /** Add a relation. */
    addRelation,
    /** Delete a relation. */
    deleteRelation,
    /** Update a node. */
    updateNode
  };
};

export const getProjectUrl = (projectId: string) => pathJoin('api/projects', projectId);
export const getProjectTemplateUrl = (projectId: string) => pathJoin('api/projects', projectId, 'template');
export const getProjectNodeProperty = (projectId: string, nodeId: string, propertyId: string) =>
  pathJoin('api/projects', projectId, 'nodes', nodeId, 'properties', propertyId);
export const getNodeDetailUrl = (projectId: string, dataNodeId: string) =>
  pathJoin('api/projects', projectId, 'nodes', dataNodeId);
export const getCreateRootNodeUrl = (projectId: string, queryId: string) =>
  pathJoin('api/projects', projectId, 'queries', queryId, 'nodes', 'root', 'data');
